import '../styles/Gallery.css';
import { useEffect, useMemo, useState } from 'react';
import { S3Client, ListObjectsV2Command } from '@aws-sdk/client-s3';
import {
	Box,
	Chip,
	Container,
	ImageList,
	ImageListItem,
	ImageListItemBar,
	Modal,
	Pagination,
	Stack,
} from '@mui/material';

const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 'auto',
	maxWidth: '600px',
	border: '2px solid #fff',
	'&:focus': {
		outline: 'none',
	},
};

const s3Client = new S3Client({
	region: process.env.REACT_APP_AWS_REGION,
	credentials: {
		accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
		secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
	},
});

const youtubeURLs = [
	'b6Yba6Tk75c',
	'JwWVxWkR5Mc',
	'ECxrGsdO77w',
	'fCNwTWLOi0Q',
	'A6Uzf2aNFRs',
	'fIjbSEYMt9c',
];

function formatTitle(filename) {
	// Remove extension
	const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
	// Remove prefix
	let name = nameWithoutExt.replace(/^high-point-resort-/, '');
	// Split by dash
	const parts = name.split('-');
	let result = '';
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		// If the part is a number, stop before adding it
		if (/^\d+$/.test(part)) break;
		// Capitalize first letter
		result +=
			(i === 0 ? '' : ' ') + part.charAt(0).toUpperCase() + part.slice(1);
	}
	return result;
}

export default function Gallery() {
	const [columns, setColumns] = useState(3);
	const [selectedChip, setSelectedChip] = useState('resort');
	const [open, setOpen] = useState(false);
	const [modalImage, setModalImage] = useState('');
	const [s3Images, setS3Images] = useState([]);
	const [localImages, setLocalImages] = useState([]);
	const [islandImages, setIslandImages] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const imagesPerPage = 12;

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	const resortImagesList = [
		'from-top.jpg',
		'front-1.jpg',
		'front-2.jpg',
		'front-3.jpg',
		'front-4.jpg',
		'outside.jpg',
		'pool-1.jpg',
		'pool-2.jpg',
		'porch.jpg',
		'rooms/bed-front.jpg',
		'rooms/double-bed-room-1.jpg',
		'rooms/double-bed-room-2.jpg',
		'rooms/king-bed.jpg',
		'rooms/living-room.jpg',
		'rooms/queen-bed-room-1.jpg',
		'rooms/queen-bed-room-2.jpg',
		'rooms/queen-bed.jpg',
		'rooms/queen-suite-a-room.jpg',
		'rooms/room.jpg',
		'rooms/window.jpg',
	];

	// Combine all images for the selected chip
	const allImages = useMemo(() => {
		if (selectedChip === 'resort') {
			return [...localImages, ...s3Images];
		}
		if (selectedChip === 'island') {
			return islandImages;
		}
		return [];
	}, [selectedChip, localImages, s3Images]);

	// Calculate pagination
	const totalPages = Math.ceil(allImages.length / imagesPerPage);
	const startIndex = (currentPage - 1) * imagesPerPage;
	const currentImages = allImages.slice(
		startIndex,
		startIndex + imagesPerPage
	);

	useEffect(() => {
		const updateColumns = () => {
			if (window.innerWidth < 900) {
				if (window.innerWidth < 600) {
					setColumns(1);
				} else {
					setColumns(2);
				}
			} else {
				setColumns(3);
			}
		};

		updateColumns();

		window.addEventListener('resize', updateColumns);

		return () => {
			window.removeEventListener('resize', updateColumns);
		};
	}, []);

	useEffect(() => {
		const fetchS3Images = async () => {
			try {
				const command = new ListObjectsV2Command({
					Bucket: process.env.REACT_APP_AWS_BUCKET,
					Prefix: 'resort/',
					MaxKeys: 1000,
				});

				const response = await s3Client.send(command);

				const BUCKET = process.env.REACT_APP_AWS_BUCKET;
				const REGION = process.env.REACT_APP_AWS_REGION;

				const resortImageObjects =
					response.Contents?.filter((obj) => {
						const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
							obj.Key
						);
						return isImage && obj.Key !== 'resort/'; // Exclude folder itself
					}).map((obj) => {
						const pathParts = obj.Key.split('/');
						const filename = pathParts.pop();
						const subfolder =
							pathParts.length > 1
								? pathParts.slice(1).join('/')
								: '';

						return {
							img: `https://${BUCKET}.s3.${REGION}.amazonaws.com/${obj.Key}`,
							title: formatTitle(filename),
							subfolder: subfolder,
							fullPath: obj.Key,
							key: obj.Key,
						};
					}) || [];

				setS3Images(resortImageObjects);
			} catch (error) {
				console.error('Error fetching S3 images:', error);
			}
		};

		fetchS3Images();
	}, []);

	useEffect(() => {
		const fetchIslandImages = async () => {
			try {
				const folders = [
					'campbell-river',
					'cape-mudge',
					'rebecca-spit',
				];
				const BUCKET = process.env.REACT_APP_AWS_BUCKET;
				const REGION = process.env.REACT_APP_AWS_REGION;

				// Create promises for all folder requests
				const folderPromises = folders.map(async (folder) => {
					const command = new ListObjectsV2Command({
						Bucket: BUCKET,
						Prefix: `${folder}/`,
						MaxKeys: 1000,
					});

					const response = await s3Client.send(command);

					return (
						response.Contents?.filter((obj) => {
							const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
								obj.Key
							);
							return isImage && obj.Key !== `${folder}/`;
						}).map((obj) => {
							const pathParts = obj.Key.split('/');
							const filename = pathParts.pop();

							return {
								img: `https://${BUCKET}.s3.${REGION}.amazonaws.com/${obj.Key}`,
								title: formatTitle(filename),
								folder: folder,
								fullPath: obj.Key,
								key: obj.Key,
							};
						}) || []
					);
				});

				// Wait for all requests to complete
				const folderResults = await Promise.all(folderPromises);

				// Flatten the array of arrays into a single array
				const allResortImages = folderResults.flat();

				setIslandImages(allResortImages);
			} catch (error) {
				console.error('Error fetching S3 images:', error);
			}
		};

		fetchIslandImages();
	}, []);

	useEffect(() => {
		// Convert the list to the format your component expects
		const formattedImages = resortImagesList.map((imagePath, index) => {
			const pathParts = imagePath.split('/');
			const filename = pathParts.pop();
			const subfolder = pathParts.length > 0 ? pathParts.join('/') : '';

			return {
				img: `/images/resort/${imagePath}`,
				title: formatTitle(filename),
				subfolder: subfolder,
				key: `resort_${index}`,
			};
		});

		setLocalImages(formattedImages);
	}, []);

	const selectChip = async (chip) => {
		setSelectedChip(chip);
	};

	// Handle page change
	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};

	// Reset to first page when chip changes
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedChip]);

	return (
		<div>
			<Box className="gallery-container">
				<div className="page-banner-wrapper">
					<h2 className="page-banner-title">Quadra Island</h2>
					<img
						className="page-banner"
						src="https://thehighpoint.s3.ca-central-1.amazonaws.com/orange-sunrise-over-quadra-island.jpg"
						alt="Quadra Island"
					/>
				</div>
				<Container
					component="main"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
						'&.MuiContainer-root': {
							px: '0',
						},
					}}
				>
					<Box
						className="gallery"
						container
						sx={{
							mx: { xs: '12px', sm: '24px' },
							marginTop: '32px',
							marginBottom: '32px',
						}}
					>
						<ImageList
							className="gallery-image-list"
							variant="quilted"
							cols={columns}
							rowHeight={250}
							sx={{
								pb: { lg: '32px' },
								overflowY: 'hidden',
							}}
						>
							<ImageListItem
								key="Subheader"
								cols={columns}
								sx={{
									height: '52px !important',
									alignContent: 'center',
									marginLeft: '10px',
								}}
							>
								<Stack direction="row" spacing={1}>
									<Chip
										label="Resort"
										variant={
											selectedChip === 'resort'
												? 'filled'
												: 'outlined'
										}
										onClick={() => selectChip('resort')}
										sx={{
											fontSize: '0.85rem',
											'&.active': {
												backgroundColor:
													'rgba(0, 0, 0, 0.08)',
											},
										}}
									/>
									<Chip
										label="Island"
										variant={
											selectedChip === 'island'
												? 'filled'
												: 'outlined'
										}
										onClick={() => selectChip('island')}
										sx={{
											fontSize: '0.85rem',
											'&.active': {
												backgroundColor:
													'rgba(0, 0, 0, 0.08)',
											},
										}}
									/>
									<Chip
										label="Videos"
										variant={
											selectedChip === 'videos'
												? 'filled'
												: 'outlined'
										}
										onClick={() => selectChip('videos')}
										sx={{
											fontSize: '0.85rem',
											'&.active': {
												backgroundColor:
													'rgba(0, 0, 0, 0.08)',
											},
										}}
									/>
								</Stack>
							</ImageListItem>
							{selectedChip === 'resort' ||
							selectedChip === 'island'
								? currentImages.map((item) => (
										<ImageListItem
											className="gallery-image-list-item"
											key={item.key}
											cols={item.cols || 1}
											rows={item.rows || 1}
											onClick={() => {
												handleOpen();
												setModalImage(item.img);
											}}
											sx={{
												'&.MuiImageListItem-root:active':
													{
														transform:
															'translateY(1.5px)',
													},
											}}
										>
											<img
												src={item.img}
												alt={item.title}
												loading="lazy"
											/>
											<ImageListItemBar
												title={item.title}
												sx={{
													'& .MuiImageListItemBar-title':
														{
															fontSize:
																'0.85rem !important',
														},
												}}
											/>
										</ImageListItem>
									))
								: selectedChip === 'videos'
									? youtubeURLs.map((videoId, index) => (
											<ImageListItem
												className="gallery-image-list-item"
												key={videoId}
												cols={1}
												rows={1}
												sx={{
													'&.MuiImageListItem-root:active':
														{
															transform:
																'translateY(1.5px)',
														},
												}}
											>
												<iframe
													width="100%"
													height="100%"
													src={`https://www.youtube.com/embed/${videoId}`}
													title={`YouTube Video ${index + 1}`}
													allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
													allowFullScreen
													style={{
														aspectRatio: '16/9',
														minHeight: '200px',
													}}
												/>
											</ImageListItem>
										))
									: null}
						</ImageList>

						{totalPages > 1 && (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									mt: 3,
									mb: 2,
								}}
							>
								<Pagination
									count={totalPages}
									page={currentPage}
									onChange={handlePageChange}
									color="primary"
									size="medium"
									showFirstButton
									showLastButton
									sx={{
										'& .Mui-selected, .Mui-selected:hover':
											{
												backgroundColor:
													'#001b42 !important',
											},
									}}
								/>
							</Box>
						)}
					</Box>
				</Container>
			</Box>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignContent: 'center',
				}}
			>
				<Box sx={style} onClick={handleClose}>
					<img className="modal-image" src={modalImage} />
				</Box>
			</Modal>
		</div>
	);
}
